import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  Database,
  ChevronLeft,
  ChevronRight,
  Filter,
  Diamond,
  Lock,
  Trash2,
  CheckCircle2,
  ShieldAlert,
  X,
} from "lucide-react";
import CircleGlow from "../components/CircleGlow";
import { leeuwardenSystems } from "../data/leeuwardenSystems";
import {
  loadAssessments,
  deleteAssessment,
  loadSeedOverrides,
  loadSeedUpdates,
} from "../lib/assessmentStore";
import { vngSteps } from "../data/vngSteps";

/**
 * /register/log — Administrative full audit log.
 *
 * Strictly admin-side: shows every assessment, version-update, and
 * publication event in a paginated table.  Citizens never reach this page.
 */

const PAGE_SIZE = 12;

const RISK_PILLS = {
  high: { label: "Onaanvaardbaar", color: "#C0392B", bg: "rgba(192,57,43,0.08)" },
  medium: {
    label: "Beperkt risico",
    color: "#C8941F",
    bg: "rgba(200,148,31,0.10)",
  },
  low: { label: "Minimaal risico", color: "#1F8A4D", bg: "rgba(31,138,77,0.10)" },
};

const RISK_FROM_LABEL = (label = "") => {
  const v = label.toLowerCase();
  if (v.includes("hoog") || v.includes("onaan")) return "high";
  if (v.includes("midden") || v.includes("medium") || v.includes("beperkt"))
    return "medium";
  return "low";
};

const MONTHS_NL = [
  "jan",
  "feb",
  "mrt",
  "apr",
  "mei",
  "jun",
  "jul",
  "aug",
  "sep",
  "okt",
  "nov",
  "dec",
];

const FORMAT_DATETIME = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const day = String(d.getDate()).padStart(2, "0");
  const month = MONTHS_NL[d.getMonth()];
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${day} ${month} ${year}, ${hh}:${mm}`;
};

const LWD_META = {
  uitkeringscheck: { lastTested: "2026-01-04T09:14:00", readiness: 88 },
  schuldhulpverlening: { lastTested: "2025-12-18T15:32:00", readiness: 72 },
  "wmo-zorg": { lastTested: "2025-11-30T11:08:00", readiness: 64 },
  handhaving: { lastTested: "2026-01-02T08:21:00", readiness: 80 },
};

const LWD_LIFECYCLE_PROGRESS = {
  Initiatie: ["initiatie"],
  "Data-analyse": ["initiatie", "data-analyse"],
  Ontwerp: ["initiatie", "data-analyse", "ontwerp"],
  Ontwikkeling: ["initiatie", "data-analyse", "ontwerp", "ontwikkeling"],
  Validatie: ["initiatie", "data-analyse", "ontwerp", "ontwikkeling", "validatie"],
  Implementatie: [
    "initiatie",
    "data-analyse",
    "ontwerp",
    "ontwikkeling",
    "validatie",
    "implementatie",
  ],
  Monitoring: [
    "initiatie",
    "data-analyse",
    "ontwerp",
    "ontwikkeling",
    "validatie",
    "implementatie",
    "monitoring",
  ],
  Evaluatie: [
    "initiatie",
    "data-analyse",
    "ontwerp",
    "ontwikkeling",
    "validatie",
    "implementatie",
    "monitoring",
    "evaluatie",
  ],
};

const RegisterLog = () => {
  const [assessments, setAssessments] = useState([]);
  const [seedOverrides, setSeedOverrides] = useState({});
  const [seedUpdates, setSeedUpdates] = useState({});
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("all"); // all | seeds | user
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    setAssessments(loadAssessments());
    setSeedOverrides(loadSeedOverrides());
    setSeedUpdates(loadSeedUpdates());
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  // Build the timeline: every assessment + every recorded update is its own entry.
  const events = useMemo(() => {
    const out = [];

    leeuwardenSystems.forEach((s) => {
      const baseDone = LWD_LIFECYCLE_PROGRESS[s.lifecycleStage] || [];
      const overrides = seedOverrides[s.id] || {};
      let okCount = 0;
      vngSteps.forEach((v) => {
        const ok = v.id in overrides ? overrides[v.id] : baseDone.includes(v.id);
        if (ok) okCount += 1;
      });
      const computedReadiness = Math.round((okCount / vngSteps.length) * 100);
      const meta = LWD_META[s.id] || {
        lastTested: "2026-01-01T00:00:00",
        readiness: computedReadiness,
      };
      // Original baseline event
      out.push({
        kind: "lwd",
        kindLabel: "Baseline",
        id: s.id,
        code: s.code,
        name: s.name,
        org: "Gemeente Leeuwarden",
        risk: RISK_FROM_LABEL(s.riskClass),
        readiness:
          Object.keys(overrides).length > 0 ? computedReadiness : meta.readiness,
        timestamp: meta.lastTested,
        note: `Initiële inrichting · ${s.lifecycleStage}`,
        deletable: false,
      });
      // Any subsequent update events
      const upd = seedUpdates[s.id];
      if (upd) {
        out.push({
          kind: "lwd",
          kindLabel: "Update",
          id: s.id,
          code: s.code,
          name: s.name,
          org: "Gemeente Leeuwarden",
          risk: RISK_FROM_LABEL(upd.risk || s.riskClass),
          readiness: computedReadiness,
          timestamp: upd.lastUpdated,
          note: upd.versionNote || "Periodieke herbeoordeling",
          deletable: false,
        });
        (upd.history || []).forEach((h) => {
          out.push({
            kind: "lwd",
            kindLabel: "Update",
            id: s.id,
            code: s.code,
            name: s.name,
            org: "Gemeente Leeuwarden",
            risk: RISK_FROM_LABEL(s.riskClass),
            readiness: computedReadiness,
            timestamp: h.updatedAt,
            note: h.versionNote,
            deletable: false,
          });
        });
      }
    });

    assessments.forEach((u) => {
      const okCount = u.vngStatus
        ? Object.values(u.vngStatus).filter(Boolean).length
        : 0;
      const readiness = Math.round((okCount / vngSteps.length) * 100);
      out.push({
        kind: "user",
        kindLabel: u.published ? "Gepubliceerd" : "Concept",
        id: u.id,
        code: u.code,
        name: u.name,
        org: u.org || "Gemeente Leeuwarden",
        risk: u.risk,
        readiness,
        timestamp: u.lastUpdated || u.publishedAt || u.createdAt,
        note: u.versionNote || "Initiële intake",
        deletable: true,
      });
    });

    return out
      .filter((e) => !!e.timestamp)
      .sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
  }, [assessments, seedOverrides, seedUpdates]);

  const filtered = useMemo(() => {
    if (filter === "seeds") return events.filter((e) => e.kind === "lwd");
    if (filter === "user") return events.filter((e) => e.kind === "user");
    return events;
  }, [events, filter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const slice = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const askDelete = (e) => {
    if (!e.deletable) return;
    setConfirmTarget({ id: e.id, name: e.name });
  };
  const cancelDelete = () => setConfirmTarget(null);
  const confirmDelete = () => {
    if (!confirmTarget) return;
    const removed = confirmTarget.name;
    deleteAssessment(confirmTarget.id);
    setAssessments(loadAssessments());
    setConfirmTarget(null);
    setToast({ tone: "success", label: `Systeem verwijderd · ${removed}` });
  };

  return (
    <div data-testid="page-register-log">
      <section className="relative overflow-hidden">
        <CircleGlow intensity={0.55} />
        <div className="rk-container relative pt-12 md:pt-16 pb-10">
          <Link
            to="/register"
            data-testid="log-back"
            className="inline-flex items-center gap-2 text-[#D4D8DF] hover:text-white text-[12px] tracking-[0.2em] uppercase mb-8"
          >
            <ArrowLeft strokeWidth={1.25} className="w-4 h-4" />
            Terug naar register
          </Link>

          <div
            className="overline mb-5 flex items-center gap-2"
            data-testid="log-eyebrow"
          >
            <span
              aria-hidden
              className="inline-block w-1.5 h-1.5 rounded-full bg-[#9FE2BF]"
              style={{
                boxShadow: "0 0 8px rgba(159,226,191,0.7)",
                animation: "rk-pulse 2.4s ease-in-out infinite",
              }}
            />
            Cockpit · Volledig audit-logboek
          </div>
          <h1
            className="text-white tracking-tight leading-[1.05] max-w-4xl text-4xl md:text-6xl"
            style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
            data-testid="log-title"
          >
            Compliance{" "}
            <em
              className="italic text-[#D4D8DF]"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              audit-logboek.
            </em>
          </h1>
          <p
            className="mt-5 text-[#D4D8DF] max-w-2xl leading-relaxed"
            data-testid="log-subtitle"
          >
            Iedere assessment, herziening en publicatie van de gemeente
            Leeuwarden — chronologisch geordend, gepagineerd, en uitsluitend
            zichtbaar voor administrators.
          </p>
        </div>
        <div className="hairline" />
      </section>

      <section
        className="rk-container py-10"
        data-testid="log-table-section"
      >
        {/* Filter row */}
        <div
          className="flex items-center justify-between mb-6"
          data-testid="log-filter-row"
        >
          <div
            className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase"
            style={{ color: "#9AA4B2" }}
          >
            <Filter strokeWidth={1.25} className="w-4 h-4" />
            Filter ·
          </div>
          <div className="flex items-center gap-2">
            {[
              { id: "all", label: "Alle" },
              { id: "seeds", label: "Baseline" },
              { id: "user", label: "Gebruikersassessments" },
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => {
                  setFilter(f.id);
                  setPage(1);
                }}
                data-testid={`log-filter-${f.id}`}
                aria-pressed={filter === f.id}
                className="px-3 py-1.5 text-[11px] tracking-[0.18em] uppercase transition-colors"
                style={{
                  border: "1px solid rgba(192,197,206,0.3)",
                  background:
                    filter === f.id
                      ? "rgba(91,192,235,0.15)"
                      : "transparent",
                  color: filter === f.id ? "#7FD7E4" : "#D4D8DF",
                  borderColor:
                    filter === f.id
                      ? "rgba(91,192,235,0.5)"
                      : "rgba(192,197,206,0.3)",
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div
          className="overflow-x-auto"
          style={{
            background: "#FFFFFF",
            color: "#0F1E33",
            border: "1px solid rgba(15,30,51,0.10)",
            boxShadow: "0 30px 60px -30px rgba(0,0,0,0.45)",
          }}
        >
          {/* Internal table header strip */}
          <div
            className="px-6 md:px-8 py-4 flex items-center justify-between"
            style={{ borderBottom: "1px solid rgba(15,30,51,0.08)" }}
          >
            <div
              className="flex items-center gap-3 text-[11px] tracking-[0.22em] uppercase"
              style={{ color: "#5470A3" }}
            >
              <Database strokeWidth={1.25} className="w-4 h-4" />
              Logboek · {filtered.length} events
            </div>
            <div
              className="text-[10px] tracking-[0.18em] uppercase"
              style={{ color: "#9AA4B2" }}
            >
              Pagina {safePage} / {totalPages}
            </div>
          </div>

          {/* Column headers */}
          <div
            className="hidden md:grid px-6 md:px-8 py-3 text-[10px] tracking-[0.22em] uppercase font-medium"
            style={{
              gridTemplateColumns: "1.2fr 0.8fr 2.4fr 1.1fr 0.9fr 0.9fr",
              color: "#5470A3",
              background: "#F4F6FA",
              borderBottom: "1px solid rgba(15,30,51,0.08)",
            }}
            data-testid="log-table-head"
          >
            <div>Datum &amp; tijd</div>
            <div>Type</div>
            <div>Systeem · notitie</div>
            <div>Risico</div>
            <div>Gereedheid</div>
            <div className="text-right">Acties</div>
          </div>

          <ul data-testid="log-table-body" className="min-w-[720px] md:min-w-0">
            {slice.map((e, idx) => (
              <EventRow
                key={`${e.kind}-${e.id}-${e.timestamp}-${idx}`}
                event={e}
                onDelete={() => askDelete(e)}
              />
            ))}
            {slice.length === 0 && (
              <li
                className="px-6 md:px-8 py-12 text-center text-sm"
                style={{ color: "#5470A3" }}
              >
                Geen events voor dit filter.
              </li>
            )}
          </ul>

          {/* Pagination */}
          {totalPages > 1 && (
            <div
              className="px-6 md:px-8 py-4 flex items-center justify-between"
              style={{ borderTop: "1px solid rgba(15,30,51,0.08)" }}
              data-testid="log-pagination"
            >
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage === 1}
                className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase disabled:opacity-30"
                style={{ color: "#1C3A5A" }}
                data-testid="log-prev"
              >
                <ChevronLeft strokeWidth={1.5} className="w-4 h-4" />
                Vorige
              </button>
              <span
                className="text-[11px] tracking-[0.18em] uppercase"
                style={{ color: "#5470A3" }}
              >
                {(safePage - 1) * PAGE_SIZE + 1}–
                {Math.min(safePage * PAGE_SIZE, filtered.length)} van{" "}
                {filtered.length}
              </span>
              <button
                type="button"
                onClick={() =>
                  setPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={safePage === totalPages}
                className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase disabled:opacity-30"
                style={{ color: "#1C3A5A" }}
                data-testid="log-next"
              >
                Volgende
                <ChevronRight strokeWidth={1.5} className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </section>

      {confirmTarget && (
        <ConfirmDialog
          name={confirmTarget.name}
          onCancel={cancelDelete}
          onConfirm={confirmDelete}
        />
      )}
      {toast && <Toast label={toast.label} />}
    </div>
  );
};

const EventRow = ({ event, onDelete }) => {
  const pill = RISK_PILLS[event.risk] || RISK_PILLS.medium;
  const isUpdate = event.kindLabel === "Update";
  const typeColor = isUpdate
    ? "#5BC0EB"
    : event.kindLabel === "Concept"
    ? "#C8941F"
    : "#1F8A4D";
  return (
    <li
      data-testid={`log-row-${event.id}-${event.timestamp}`}
      style={{ borderBottom: "1px solid rgba(15,30,51,0.06)" }}
      className="hover:bg-[#F4F6FA] transition-colors duration-200"
    >
      <Link
        to={`/platform/${event.id}`}
        className="grid items-center px-6 md:px-8 py-5 gap-4"
        style={{ gridTemplateColumns: "1.2fr 0.8fr 2.4fr 1.1fr 0.9fr 0.9fr" }}
      >
        <div>
          <div
            className="text-sm"
            style={{
              fontFamily: "Fraunces, serif",
              fontWeight: 400,
              color: "#0F1E33",
            }}
          >
            {FORMAT_DATETIME(event.timestamp)}
          </div>
          <div
            className="mt-1 text-[10px] tracking-[0.18em] uppercase"
            style={{ color: "#5470A3" }}
          >
            {event.code}
          </div>
        </div>

        <div>
          <span
            className="inline-flex items-center px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase"
            style={{
              border: `1px solid ${typeColor}`,
              color: typeColor,
              background: `${typeColor}14`,
            }}
          >
            {event.kindLabel}
          </span>
        </div>

        <div className="min-w-0">
          <div
            className="text-base truncate"
            style={{
              fontFamily: "Fraunces, serif",
              fontWeight: 500,
              color: "#0F1E33",
            }}
          >
            {event.name}
          </div>
          <div className="mt-0.5 text-[12px] truncate" style={{ color: "#5470A3" }}>
            {event.org} · {event.note}
          </div>
        </div>

        <div>
          <span
            className="inline-flex items-center gap-2 px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase"
            style={{
              border: `1px solid ${pill.color}`,
              color: pill.color,
              background: pill.bg,
              fontWeight: 500,
            }}
          >
            <Diamond
              strokeWidth={1.5}
              className="w-3 h-3"
              style={{ color: pill.color }}
            />
            {pill.label}
          </span>
        </div>

        <div>
          <span
            className="inline-flex items-center justify-center w-12 h-12 rounded-full text-base"
            style={{
              fontFamily: "Fraunces, serif",
              fontWeight: 500,
              border: "1px solid #5470A3",
              color: "#0F1E33",
            }}
          >
            {Math.round(event.readiness)}
          </span>
        </div>

        <div
          className="flex items-center gap-3 justify-end"
          onClick={(e) => e.stopPropagation()}
        >
          <span
            className="inline-flex items-center gap-1 text-[11px] tracking-[0.2em] uppercase"
            style={{ color: "#1C3A5A" }}
          >
            Open
            <ArrowUpRight strokeWidth={1.5} className="w-3.5 h-3.5" />
          </span>
          {event.deletable ? (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDelete();
              }}
              aria-label="Verwijder systeem"
              data-testid={`log-action-delete-${event.id}`}
              className="w-8 h-8 inline-flex items-center justify-center"
              style={{
                border: "1px solid rgba(192, 57, 43, 0.3)",
                color: "#C0392B",
                background: "rgba(192, 57, 43, 0.05)",
              }}
            >
              <Trash2 strokeWidth={1.25} className="w-4 h-4" />
            </button>
          ) : (
            <span
              aria-label="Vast in handover"
              className="w-8 h-8 inline-flex items-center justify-center"
              style={{
                border: "1px solid rgba(15,30,51,0.12)",
                color: "rgba(15,30,51,0.3)",
              }}
            >
              <Lock strokeWidth={1.25} className="w-3.5 h-3.5" />
            </span>
          )}
        </div>
      </Link>
    </li>
  );
};

const ConfirmDialog = ({ name, onCancel, onConfirm }) => (
  <div
    role="dialog"
    aria-modal="true"
    aria-label="Bevestig verwijderen"
    data-testid="log-confirm-overlay"
    className="fixed inset-0 z-50 flex items-center justify-center"
    style={{ background: "rgba(15, 30, 51, 0.72)", backdropFilter: "blur(6px)" }}
    onClick={onCancel}
  >
    <div
      className="relative max-w-lg w-[92%] rk-fade-up"
      style={{
        background: "#162E4A",
        border: "1px solid rgba(192,197,206,0.25)",
        boxShadow: "0 30px 80px -20px rgba(0,0,0,0.6)",
        padding: "2.5rem 2rem 2rem",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={onCancel}
        className="absolute top-4 right-4 text-[#9AA4B2] hover:text-white"
        aria-label="Sluit"
      >
        <X strokeWidth={1.25} className="w-5 h-5" />
      </button>
      <div className="flex items-start gap-4">
        <div
          className="w-12 h-12 border border-[#F08080]/55 flex items-center justify-center shrink-0"
          aria-hidden
        >
          <ShieldAlert
            strokeWidth={1}
            className="w-6 h-6"
            style={{ color: "#F08080" }}
          />
        </div>
        <div>
          <div className="overline mb-3" style={{ color: "#F08080" }}>
            Bevestig verwijdering
          </div>
          <h2
            className="text-2xl md:text-3xl text-white tracking-tight"
            style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
          >
            Weet u zeker dat u{" "}
            <em
              className="italic text-[#D4D8DF]"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              {name}
            </em>{" "}
            definitief wilt verwijderen?
          </h2>
          <p className="mt-4 text-sm text-[#9AA4B2] leading-relaxed">
            Deze actie verwijdert het assessment, de bijbehorende
            register-publicatie en het volledige lineage. Het kan niet ongedaan
            worden gemaakt.
          </p>
        </div>
      </div>
      <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="rk-btn-ghost"
          style={{
            justifyContent: "center",
            border: "1px solid rgba(192,197,206,0.4)",
            padding: "0.7rem 1.2rem",
          }}
        >
          Annuleren
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="rk-btn-danger"
          autoFocus
        >
          <Trash2 strokeWidth={1.25} className="w-4 h-4" />
          Definitief verwijderen
        </button>
      </div>
    </div>
  </div>
);

const Toast = ({ label }) => (
  <div
    role="status"
    aria-live="polite"
    data-testid="log-toast"
    className="fixed bottom-8 right-8 z-50 rk-fade-up"
    style={{
      background: "#162E4A",
      border: "1px solid rgba(159,226,191,0.55)",
      padding: "0.85rem 1.1rem",
      display: "flex",
      alignItems: "center",
      gap: "0.7rem",
      boxShadow: "0 24px 48px -16px rgba(0,0,0,0.6)",
      maxWidth: "min(94vw, 420px)",
    }}
  >
    <CheckCircle2
      strokeWidth={1.5}
      className="w-5 h-5"
      style={{ color: "#9FE2BF" }}
    />
    <span className="text-sm text-white">{label}</span>
  </div>
);

export default RegisterLog;
